/**
 * AppSelectorScreen (DQGMS · mockup)
 *
 * Shown once, right after login, when the user hasn't yet picked which
 * app experience they want:
 *   - QGS  → the new DQGMS mockup (Gate Select, TriState, Finishing, etc.)
 *   - QAS  → the legacy behaviour (dashboard direct, binary checkbox, no Finishing)
 *
 * The choice is persisted to localStorage under 'dqgms_app_mode' so it
 * survives refreshes. A "Switch app" link in the header lets the operator
 * come back and pick the other one.
 *
 * Feature-flag: only reachable when REACT_APP_USE_MOCK === 'true'.
 * Outside the mockup build the selector is never rendered.
 */
import React from 'react';

const CARDS = [
  {
    key: 'QGS',
    title: 'QGS App',
    subtitle: 'Digital Quality Gate System',
    tagline: 'New — with gate selection, tri-state responses (OK / Rework / KO) and the Finishing branch.',
    color: '#0a86c4',
    bg: '#e6f2fa',
    icon: '🚦',
    badge: 'NEW',
    badgeBg: '#0a86c4',
    disabled: false,
  },
  {
    key: 'QAS',
    title: 'QAS App',
    subtitle: 'Quality Audit System · B-Kansa sampling',
    tagline: 'Current — the existing binary-checkbox flow used for the B-Kansa sampling audit.',
    color: '#4a596b',
    bg: '#eef1f5',
    icon: '📋',
    badge: 'CURRENT',
    badgeBg: '#4a596b',
    disabled: false,
  },
];

export default function AppSelectorScreen({ onSelect, user }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f4f7fa 0%, #e5eaf0 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            fontSize: 11, letterSpacing: 1.4, color: '#8592a3',
            textTransform: 'uppercase', marginBottom: 8,
          }}>
            {user && user.username ? `Welcome, ${user.username}` : 'Welcome'}
          </div>
          <h1 style={{
            margin: 0, fontSize: 26, fontWeight: 800,
            color: '#17253d', letterSpacing: '-0.01em',
          }}>Which app are you using today?</h1>
          <p style={{
            margin: '8px 0 0 0', color: '#4a596b', fontSize: 14, maxWidth: 520,
            marginLeft: 'auto', marginRight: 'auto',
          }}>
            Choose once — you can switch anytime from the header.
          </p>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          {CARDS.map(c => (
            <button
              key={c.key}
              onClick={() => { if (!c.disabled) onSelect(c.key); }}
              disabled={c.disabled}
              aria-disabled={c.disabled || undefined}
              title={c.disabled ? 'Not available yet in this mockup' : undefined}
              style={{
                background: c.disabled ? '#fafbfc' : '#fff',
                border: `2px solid ${c.disabled ? '#d9dfe6' : c.color}`,
                borderRadius: 14,
                padding: 22,
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                cursor: c.disabled ? 'not-allowed' : 'pointer',
                textAlign: 'left',
                transition: 'transform 0.1s, box-shadow 0.1s',
                boxShadow: '0 2px 6px rgba(15,34,55,0.06)',
                opacity: c.disabled ? 0.65 : 1,
              }}
              onMouseEnter={e => {
                if (c.disabled) return;
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 10px 22px rgba(15,34,55,0.14)';
              }}
              onMouseLeave={e => {
                if (c.disabled) return;
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,34,55,0.06)';
              }}
            >
              <div style={{
                width: 68, height: 68, borderRadius: 14,
                background: c.bg, color: c.color,
                display: 'grid', placeItems: 'center', fontSize: 32,
                flexShrink: 0,
                filter: c.disabled ? 'grayscale(0.15)' : 'none',
              }}>{c.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.title}</div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    background: c.badgeBg, color: '#fff',
                    padding: '3px 8px', borderRadius: 999,
                  }}>{c.badge}</span>
                </div>
                <div style={{ fontSize: 13, color: '#4a596b', marginBottom: 4, fontWeight: 600 }}>{c.subtitle}</div>
                <div style={{ fontSize: 13, color: '#4a596b', lineHeight: 1.4 }}>{c.tagline}</div>
              </div>
              <div style={{
                fontSize: c.disabled ? 12 : 28,
                color: c.color,
                fontWeight: c.disabled ? 700 : 300,
                flexShrink: 0,
                letterSpacing: c.disabled ? 1 : 0,
                textTransform: c.disabled ? 'uppercase' : 'none',
              }}>{c.disabled ? 'Soon' : '›'}</div>
            </button>
          ))}
        </div>

        <div style={{
          marginTop: 24, textAlign: 'center',
          fontSize: 11, color: '#8592a3',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace', letterSpacing: 0.4,
        }}>
          Mockup build — either choice runs against a mocked backend.
        </div>
      </div>
    </div>
  );
}
